import { useCallback } from 'react'
import Safe, { SafeConfig } from '@safe-global/protocol-kit'
import {
  getMinimalEIP1193Provider,
  createPredictionConfig,
  createConnectionConfig,
} from '../utils/helpers'
import { SafeDeployStep, PendingSafeStatus, PayMethod } from '../utils/types'
import {
  decodeEventLog,
  Chain,
  zeroAddress,
  createWalletClient,
  createPublicClient,
  custom,
  http,
  getAddress,
} from 'viem'
import { privateKeyToAccount } from 'viem/accounts'
import { waitForTransactionReceipt } from 'viem/actions'
import { useAccount, useSwitchChain, usePublicClient } from 'wagmi'
import { useSafeWalletContext } from '../provider/SafeWalletContext'
import { DEFAULT_DEPLOY_STEPS } from '../utils/constants'
import { getEnv } from '@/config/env'
import OperationalSafeFactoryAbi from '@/abis/OperationalSafeFactory.json'
import type { Abi } from 'viem'
import { useWallet } from '@/hooks/useWallet'
import { getDemoPrivateKey } from '@/utils/demoWallet'
import { getRpcUrlForChainId } from '@/config/env'

export default function useNewSafe() {
  const { address: accountAddress, connector } = useAccount()
  const { address: walletAddress } = useWallet()
  const { switchChain } = useSwitchChain()
  const { addSafe, contractNetworks } = useSafeWalletContext()
  const publicClient = usePublicClient()
  const demoPrivateKey = getDemoPrivateKey()
  const signer = walletAddress || accountAddress
  const signerAddress = signer ? getAddress(signer as `0x${string}`) : undefined

  const predictNewSafeAddress = useCallback(
    async (
      owners: `0x${string}`[],
      threshold: number,
      chain: Chain,
      saltNonce: string
    ): Promise<{ address: `0x${string}`; isDeployed: boolean }> => {
      let kit: Safe | null = null
      try {
        const config: SafeConfig = createPredictionConfig(
          chain.rpcUrls.default.http[0],
          signer,
          owners,
          threshold,
          saltNonce,
          contractNetworks
        )
        kit = await Safe.init(config)
        if (kit) {
          const safeAddress = await kit.getAddress()
          const isDeployed = await kit.isSafeDeployed()
          return { address: safeAddress as `0x${string}`, isDeployed }
        }
      } catch {
        return { address: zeroAddress, isDeployed: false }
      }
      return { address: zeroAddress, isDeployed: false }
    },
    [signer, contractNetworks]
  )

  const predictOperationalSafeAddress = useCallback(
    async (
      owners: `0x${string}`[],
      threshold: number,
      chain: Chain,
      nonce: number
    ): Promise<{ address: `0x${string}`; isDeployed: boolean }> => {
      try {
        if (!signerAddress) {
          return { address: zeroAddress, isDeployed: false }
        }

        const chainPublicClient = createPublicClient({
          chain,
          transport: http(chain.rpcUrls.default.http[0]),
        })

        const env = getEnv()
        const operationalSafeFactory =
          env.operationalSafeFactory as `0x${string}`

        const result = await chainPublicClient.simulateContract({
          address: operationalSafeFactory,
          abi: OperationalSafeFactoryAbi as Abi,
          functionName: 'createOperationalSafe',
          args: [owners, BigInt(threshold), BigInt(nonce)],
          account: signerAddress,
        })

        const predictedAddress = getAddress(result.result as `0x${string}`)

        const code = await chainPublicClient.getCode({
          address: predictedAddress,
        })
        const isDeployed = Boolean(code && code !== '0x')

        return { address: predictedAddress, isDeployed }
      } catch {
        return { address: zeroAddress, isDeployed: false }
      }
    },
    [signerAddress]
  )

  const deployNewSafe = useCallback(
    async (
      owners: `0x${string}`[],
      threshold: number,
      chain: Chain,
      saltNonce: string | undefined,
      safeName: string,
      setDeploySteps: (steps: SafeDeployStep[]) => void
    ): Promise<SafeDeployStep[]> => {
      const steps: SafeDeployStep[] = DEFAULT_DEPLOY_STEPS.map((step) => ({
        ...step,
      }))
      try {
        steps[0].status = 'running'
        setDeploySteps([...steps])
        switchChain?.({ chainId: chain.id })
        const provider = demoPrivateKey
          ? getRpcUrlForChainId(chain.id)
          : await getMinimalEIP1193Provider(connector)
        if (!provider) {
          steps[0].status = 'error'
          steps[0].error = 'No provider found'
          setDeploySteps([...steps])
          return steps
        }
        const config: SafeConfig = createPredictionConfig(
          provider,
          demoPrivateKey ?? signerAddress,
          owners,
          threshold,
          saltNonce,
          contractNetworks
        )
        const kit = await Safe.init(config)
        let deploymentTx, kitClient, txHash
        try {
          deploymentTx = await kit.createSafeDeploymentTransaction()
          kitClient = await kit.getSafeProvider().getExternalSigner()
          steps[0].status = 'success'
          steps[1].status = 'running'
          setDeploySteps([...steps])
        } catch (err) {
          steps[0].status = 'error'
          steps[0].error = err instanceof Error ? err.message : String(err)
          setDeploySteps([...steps])
          return steps
        }
        try {
          txHash = await kitClient!.sendTransaction({
            to: deploymentTx.to as `0x${string}`,
            value: BigInt(deploymentTx.value),
            data: deploymentTx.data as `0x${string}`,
            chain: chain,
          })
          steps[1].status = 'success'
          steps[1].txHash = txHash
          steps[2].status = 'running'
          setDeploySteps([...steps])
        } catch (err) {
          steps[1].status = 'error'
          let errorMessage = ''
          if (err instanceof Error) {
            errorMessage = err.message
          } else if (typeof err === 'object' && err !== null) {
            if ('message' in err) {
              errorMessage = String(err.message)
            } else if ('code' in err) {
              const code = (err as { code: unknown }).code
              if (code === 4001) {
                errorMessage = 'User rejected the transaction'
              } else {
                errorMessage = String(err)
              }
            } else {
              errorMessage = String(err)
            }
          } else {
            errorMessage = String(err)
          }
          steps[1].error = errorMessage
          setDeploySteps([...steps])
          return steps
        }
        try {
          if (txHash) {
            await waitForTransactionReceipt(kitClient!, { hash: txHash })
            steps[2].status = 'success'
            steps[2].txHash = txHash
            steps[3].status = 'running'
            setDeploySteps([...steps])
          }
        } catch (err) {
          steps[2].status = 'error'
          steps[2].error = err instanceof Error ? err.message : String(err)
          setDeploySteps([...steps])
          return steps
        }
        try {
          const safeAddress = await kit.getAddress()
          const newKit = await kit.connect({ safeAddress })
          const isDeployed = await newKit.isSafeDeployed()
          if (!isDeployed) throw new Error('Safe deployment not detected')
          steps[3].status = 'success'
          steps[3].txHash = txHash
          setDeploySteps([...steps])
          if (isDeployed) {
            addSafe(
              String(chain.id),
              safeAddress,
              safeName,
              undefined,
              owners,
              threshold
            )
          } else {
            const chainContracts = contractNetworks
              ? contractNetworks[String(chain.id)]
              : {}
            addSafe(String(chain.id), safeAddress, safeName, {
              props: {
                factoryAddress: chainContracts?.safeProxyFactoryAddress || '',
                masterCopy: chainContracts?.safeSingletonAddress || '',
                safeAccountConfig: {
                  owners,
                  threshold,
                  fallbackHandler: chainContracts?.fallbackHandlerAddress || '',
                },
                saltNonce: saltNonce || '',
                safeVersion: '1.4.1',
              },
              status: {
                status: PendingSafeStatus.AWAITING_EXECUTION,
                type: PayMethod.PayLater,
              },
            })
          }
        } catch (err) {
          steps[3].status = 'error'
          steps[3].error = err instanceof Error ? err.message : String(err)
          steps[3].txHash = txHash
          setDeploySteps([...steps])
          return steps
        }
      } catch (err) {
        steps[0].status = 'error'
        steps[0].error = err instanceof Error ? err.message : String(err)
        setDeploySteps([...steps])
      }
      return steps
    },
    [
      connector,
      signerAddress,
      addSafe,
      switchChain,
      contractNetworks,
      demoPrivateKey,
    ]
  )

  const connectNewSafe = useCallback(
    async (
      safeAddress: `0x${string}`,
      chainId: number
    ): Promise<
      { owners: string[]; threshold: number } | { error: string } | null
    > => {
      try {
        if (!signerAddress || !chainId) {
          return { error: 'Missing signer or chainId.' }
        }
        switchChain?.({ chainId })
        const provider = demoPrivateKey
          ? getRpcUrlForChainId(chainId)
          : await getMinimalEIP1193Provider(connector)
        if (!provider) {
          return {
            error: 'Could not get EIP-1193 provider for selected network.',
          }
        }
        const config = createConnectionConfig(
          provider,
          demoPrivateKey ?? signerAddress,
          safeAddress,
          contractNetworks
        )
        let kit
        try {
          kit = await Safe.init(config)
        } catch (err) {
          return {
            error:
              'Failed to initialize Safe SDK: ' +
              (err instanceof Error ? err.message : String(err)),
          }
        }
        let deployed
        try {
          deployed = await kit.isSafeDeployed()
        } catch (err) {
          return {
            error:
              'Failed to check Safe deployment: ' +
              (err instanceof Error ? err.message : String(err)),
          }
        }
        if (!deployed) {
          return { error: 'Safe is not deployed on the selected network.' }
        }
        let owners, threshold
        try {
          owners = await kit.getOwners()
          threshold = await kit.getThreshold()
        } catch (err) {
          return {
            error:
              'Failed to fetch Safe owners/threshold: ' +
              (err instanceof Error ? err.message : String(err)),
          }
        }
        return { owners, threshold }
      } catch (err) {
        return {
          error:
            'Unexpected error: ' +
            (err instanceof Error ? err.message : String(err)),
        }
      }
    },
    [connector, signerAddress, switchChain, contractNetworks, demoPrivateKey]
  )

  const deployOperationalSafe = useCallback(
    async (
      owners: `0x${string}`[],
      threshold: number,
      chain: Chain,
      nonce: number,
      safeName: string,
      setDeploySteps: (steps: SafeDeployStep[]) => void
    ): Promise<SafeDeployStep[]> => {
      const steps: SafeDeployStep[] = DEFAULT_DEPLOY_STEPS.map((step) => ({
        ...step,
      }))
      try {
        steps[0].status = 'running'
        setDeploySteps([...steps])
        switchChain?.({ chainId: chain.id })

        const env = getEnv()
        const operationalSafeFactory =
          env.operationalSafeFactory as `0x${string}`

        if (!signerAddress || !publicClient) {
          steps[0].status = 'error'
          steps[0].error = 'No provider or signer found'
          setDeploySteps([...steps])
          return steps
        }

        let walletClient
        if (demoPrivateKey) {
          const rpcProvider = getRpcUrlForChainId(chain.id)
          if (!rpcProvider) {
            steps[0].status = 'error'
            steps[0].error = 'No provider found'
            setDeploySteps([...steps])
            return steps
          }
          walletClient = createWalletClient({
            account: privateKeyToAccount(demoPrivateKey),
            chain,
            transport: http(rpcProvider),
          })
        } else {
          const eipProvider = await getMinimalEIP1193Provider(connector)
          if (!eipProvider) {
            steps[0].status = 'error'
            steps[0].error = 'No provider found'
            setDeploySteps([...steps])
            return steps
          }
          walletClient = createWalletClient({
            account: signerAddress,
            chain,
            transport: custom(eipProvider),
          })
        }

        steps[0].status = 'success'
        steps[1].status = 'running'
        setDeploySteps([...steps])

        let txHash: `0x${string}`
        try {
          txHash = await walletClient.writeContract({
            address: operationalSafeFactory,
            abi: OperationalSafeFactoryAbi as Abi,
            functionName: 'createOperationalSafe',
            args: [owners, BigInt(threshold), BigInt(nonce)],
            chain,
            account: demoPrivateKey
              ? privateKeyToAccount(demoPrivateKey)
              : signerAddress,
          })
          steps[1].status = 'success'
          steps[1].txHash = txHash
          steps[2].status = 'running'
          setDeploySteps([...steps])
        } catch (err) {
          steps[1].status = 'error'
          steps[1].error = err instanceof Error ? err.message : String(err)
          setDeploySteps([...steps])
          return steps
        }

        let receipt
        try {
          receipt = await publicClient.waitForTransactionReceipt({
            hash: txHash,
          })
          steps[2].status = 'success'
          steps[2].txHash = txHash
          steps[3].status = 'running'
          setDeploySteps([...steps])
        } catch (err) {
          steps[2].status = 'error'
          steps[2].error = err instanceof Error ? err.message : String(err)
          steps[2].txHash = txHash
          setDeploySteps([...steps])
          return steps
        }

        let safeAddress: `0x${string}` | null = null
        try {
          const event = receipt.logs
            .map((log) => {
              try {
                return decodeEventLog({
                  abi: OperationalSafeFactoryAbi as Abi,
                  data: log.data,
                  topics: log.topics,
                })
              } catch {
                return null
              }
            })
            .find(
              (decoded) =>
                decoded && decoded.eventName === 'OperationalSafeCreated'
            ) as
            | {
                eventName: 'OperationalSafeCreated'
                args: {
                  operationalSafe: `0x${string}`
                  owners: readonly `0x${string}`[]
                  threshold: bigint
                  module: `0x${string}`
                }
              }
            | undefined

          if (event && event.args.operationalSafe) {
            safeAddress = getAddress(event.args.operationalSafe)
          } else {
            // Fallback: try to read the return value from the transaction
            // Since createOperationalSafe returns an address, we can simulate the call
            const result = await publicClient.simulateContract({
              address: operationalSafeFactory,
              abi: OperationalSafeFactoryAbi as Abi,
              functionName: 'createOperationalSafe',
              args: [owners, BigInt(threshold), BigInt(nonce)],
              account: signerAddress,
            })
            safeAddress = result.result as `0x${string}`
          }

          if (!safeAddress) {
            throw new Error('Could not determine Safe address from transaction')
          }

          steps[3].status = 'success'
          steps[3].txHash = txHash
          setDeploySteps([...steps])

          addSafe(
            String(chain.id),
            safeAddress,
            safeName,
            undefined,
            owners,
            threshold
          )
        } catch (err) {
          steps[3].status = 'error'
          steps[3].error = err instanceof Error ? err.message : String(err)
          steps[3].txHash = txHash
          setDeploySteps([...steps])
          return steps
        }
      } catch (err) {
        steps[0].status = 'error'
        steps[0].error = err instanceof Error ? err.message : String(err)
        setDeploySteps([...steps])
      }
      return steps
    },
    [connector, signerAddress, switchChain, addSafe, publicClient, demoPrivateKey]
  )

  return {
    predictNewSafeAddress,
    predictOperationalSafeAddress,
    deployNewSafe,
    deployOperationalSafe,
    connectNewSafe,
  }
}
